import Cookies from 'js-cookie'

/**
 * This is a rudimentary function to interact with the mercury-web-backend.
 * It expects everything to be manually typed and is not very robust. It should
 * be good enough to make requests for the FEM. We should eventually replace
 * this with a more automated system
 */

import {type EmptyObject} from 'react-hook-form'
import {mercuryWebBackendUrl} from '~/config/app'
import {env} from '~/config/env.mjs'
import {helpEmail} from '~/data/mercury'
import {notifyBugsnag} from '~/utils/Bugsnag/notify'
import {SimpleAPIUnknownError} from './errors/SimpleAPIUnknownError'
import {XSRF_TOKEN_COOKIE_NAME} from '~/config/cookies'
import {SimpleAPIInvalidCSRFError} from './errors/SimpleAPIInvalidCSRFError'

export enum Method {
  GET,
  POST,
}

export type Revalidate = number | 'on-demand' | 'client-only'

type FetchOptions<Params, PathPieces> = {
  fetchOptions?: RequestInit
  params: Params
  pathPieces: PathPieces
}

function getXSRFTokenHeaders(opts: {
  revalidate: Revalidate
  method: Method
}): EmptyObject | {'X-XSRF-TOKEN': string} {
  const {method, revalidate} = opts
  if (typeof window === 'undefined') return {}
  if (revalidate !== 'client-only') return {}
  if (method === Method.GET) return {}

  const existingXSRFToken = Cookies.get(XSRF_TOKEN_COOKIE_NAME)
  if (existingXSRFToken) return {'X-XSRF-TOKEN': existingXSRFToken}

  return {}
}

async function buildError(req: Response) {
  const res = await req.text()

  try {
    const json = JSON.parse(res)
    if (
      req.status === 403 &&
      json.errors.permissionDenied?.[0]?.startsWith('A valid CSRF token')
    ) {
      return new SimpleAPIInvalidCSRFError({
        rawResponseData: res,
        statusCode: req.status,
        statusText: req.statusText,
      })
    }
  } catch {
    // do nothing, response is not valid JSON
  }

  return new SimpleAPIUnknownError({
    contextualMessage: `We couldn't reach our servers. Please try refreshing the page or contact ${helpEmail} for help.`,
    rawResponseData: res,
    statusCode: req.status,
    statusText: req.statusText,
  })
}

export abstract class SimpleAPI<
  Response,
  Params = EmptyObject,
  PathPieces = EmptyObject,
> {
  abstract method: Method
  /**
   * `revalidate` is the number of seconds before the cache is invalidated.
   * If you elect to use `on-demand` you are responsible for manually calling the `revalidate` functions.
   * If you select `client-only`, then you are asserting that this request is only made on the client (e.g. form submit or with react-query)
   * See: https://nextjs.org/docs/app/building-your-application/data-fetching/fetching-caching-and-revalidating#revalidating-data
   */
  abstract revalidate: Revalidate

  abstract getPath(pathPieces: PathPieces): string

  protected abstract getMockResponse(
    fetchOptions: FetchOptions<Params, PathPieces>
  ): Promise<Response>

  protected preProcessPostParams(params: Params): Params {
    return params
  }

  async fetch(
    opts: FetchOptions<Params, PathPieces>,
    retryCount: number = 0
  ): Promise<Response> {
    const {params, pathPieces, fetchOptions} = opts
    if (env.NEXT_PUBLIC_BACKEND_SOURCE === 'mock') {
      return await this.getMockResponse(opts)
    }

    /**
     * See the comment on `revalidate` for more information.
     * If a number is provided then we want to revalidate based on time.
     * Otherwise we are either revalidating on-demand or not at all.
     */
    const nextOptions =
      typeof this.revalidate === 'number' ? {revalidate: this.revalidate} : {}

    const xsrfTokenHeaders = getXSRFTokenHeaders({
      revalidate: this.revalidate,
      method: this.method,
    })

    const req = await fetch(`${mercuryWebBackendUrl}${this.getPath(pathPieces)}`, {
      method: fetchOptions?.method ?? Method[this.method],
      headers: {
        'Content-Type': 'application/json',
        ...xsrfTokenHeaders,
        ...fetchOptions?.headers,
      },
      credentials: 'include',
      body:
        this.method === Method.GET
          ? undefined
          : JSON.stringify(this.preProcessPostParams(params)),
      next: nextOptions,
    })

    if (!req.ok) {
      // TODO(CP-217): Handle the different status codes to surface the correct error type.
      // For now we are just throwing an unknown error

      const error = await buildError(req)

      if (error instanceof SimpleAPIInvalidCSRFError && retryCount <= 2) {
        // If we get a CSRF error, we want to try to get a new token and retry the request
        // This is because the CSRF token may have expired
        // We don't want to do this indefinitely, so we will only retry once
        // If we get another CSRF error, we will throw the error
        return await this.fetch(opts, retryCount + 1)
      }

      /**
       * If it's a CSRF error, we don't want to report it to bugsnag.
       * Above, we will retry the request up to three times if we get a CSRF error.
       */
      if (error instanceof SimpleAPIInvalidCSRFError) {
        throw error
      }

      /**
       * This differs from mercury-web. MW reports the error at the useQuery/useMutation level.
       * However since Next.js supports SSG, we may not always use those hooks. As a result the error handling
       * has been moved here.
       *
       * TODO(CP-217): Add filters, for example we don't want to report CSRF related errors to bugsnag
       */
      notifyBugsnag(`SimpleAPI error: ${error.toHumanString()}`, {
        caughtError: error,
      })

      throw error
    }

    if (req.status === 204 || req.headers.get('content-length') === '0') {
      // If the server returns a 204, it means there is no content in the response
      // And MWB is not consistent and sometimes returns empty responses :(
      return {} as Response
    }

    const res = await req.json()

    return res as Response
  }
}
